import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileAudio, Upload, X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";

const CHUNK = 5 * 1024 * 1024;
const RETRIES = 4;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// retry on network drops and server/proxy 5xx; give up immediately on 4xx (bad request, auth, too large)
const retryable = (e) => !e?.response || e.response.status >= 500;
async function withRetry(fn, onRetry) {
  for (let attempt = 1; ; attempt++) {
    try { return await fn(); }
    catch (e) {
      if (attempt > RETRIES || !retryable(e)) throw e;
      onRetry?.(attempt);
      await sleep(Math.min(8000, 1000 * 2 ** (attempt - 1)));
    }
  }
}
const mb = (n) => `${(n / 1048576).toFixed(1)} MB`;
export const fmtDuration = (secs) => { const s = Math.round(secs || 0); const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60; return h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`; };
const readDuration = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const audio = document.createElement("audio");
  audio.preload = "metadata";
  const done = (v) => { URL.revokeObjectURL(url); resolve(v); };
  audio.onloadedmetadata = () => done(Number.isFinite(audio.duration) ? audio.duration : null);
  audio.onerror = () => done(null);
  audio.src = url;
});

export const AudioUpload = ({ value, onChange, testId = "audio-upload", base = "/portal/uploads", maxMb = 150 }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [dragging, setDragging] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    const ok = /\.(mp3|m4a|wav)$/i.test(file.name) || file.type.startsWith("audio/");
    if (!ok) return toast.error("Please choose an audio file (MP3 recommended)");
    if (file.size > maxMb * 1024 * 1024) return toast.error(`Audio must be under ${maxMb} MB${maxMb === 150 ? " — Acast's limit. Try exporting at 128 kbps." : ""}`);
    setProgress(0);
    try {
      const durationPromise = readDuration(file);
      const { data } = await api.post(`${base}/init`, { filename: file.name, size: file.size, content_type: file.type || "audio/mpeg" });
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        await withRetry(() => {
          const form = new FormData();
          form.append("index", i);
          form.append("chunk", file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK)));
          return api.post(`${base}/${data.upload_id}/chunk`, form, { timeout: 120000, onUploadProgress: (e) => setProgress(Math.min(99, Math.round(((i * CHUNK + e.loaded) / file.size) * 100))) });
        }, (attempt) => setRetrying({ part: i + 1, total, attempt }));
        setRetrying(null);
      }
      const { data: done } = await withRetry(() => api.post(`${base}/${data.upload_id}/complete`, null, { timeout: 300000 }), (attempt) => setRetrying({ part: total, total, attempt, finishing: true }));
      const duration = (await durationPromise) || done.duration || null;
      onChange({ upload_id: data.upload_id, filename: file.name, size: file.size, duration });
      toast.success("Audio uploaded");
    } catch (e) { toast.error(`${errorMessage(e)} — the upload was retried ${RETRIES} times. Check your connection and try again.`); }
    finally { setProgress(null); setRetrying(null); }
  };

  if (value) return (
    <div className="flex items-center gap-3 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-3" data-testid={`${testId}-done`}>
      <FileAudio size={20} className="text-emerald-300 shrink-0" />
      <div className="min-w-0 flex-1"><p className="text-sm font-semibold truncate">{value.filename}</p><p className="text-xs text-slate-400">{mb(value.size)}{value.duration ? <> · <span data-testid={`${testId}-duration`}>{fmtDuration(value.duration)}</span></> : ""} · uploaded</p></div>
      <button type="button" onClick={() => onChange(null)} className="p-1.5 text-slate-400 hover:text-rose-400" aria-label="Remove audio" data-testid={`${testId}-remove`}><X size={16} /></button>
    </div>
  );

  return (
    <div data-testid={testId}>
      <button type="button" onClick={() => inputRef.current?.click()} disabled={progress !== null}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files?.[0]); }}
        className={`w-full rounded-xl border-2 border-dashed p-6 text-center transition-colors ${dragging ? "border-sky-400 bg-sky-400/10" : "border-white/15 bg-white/[0.03] hover:border-sky-400/60"}`} data-testid={`${testId}-dropzone`}>
        {progress === null ? (
          <><Upload size={22} className="mx-auto text-sky-300" /><p className="mt-2 text-sm font-semibold">Drop your MP3 here or click to choose</p><p className="mt-1 text-xs text-slate-500">MP3 at ~128 kbps · up to {maxMb} MB</p></>
        ) : (
          <><p className="text-sm font-semibold">{retrying ? (retrying.finishing ? `Finishing up — retrying (attempt ${retrying.attempt + 1} of ${RETRIES + 1})…` : `Connection hiccup — retrying part ${retrying.part} of ${retrying.total} (attempt ${retrying.attempt + 1} of ${RETRIES + 1})…`) : `Uploading… ${progress}%`}</p><div className="mt-3 h-2 w-full rounded-full bg-white/10 overflow-hidden"><div className={`h-full transition-[width] ${retrying ? "bg-amber-400" : "bg-sky-400"}`} style={{ width: `${progress}%` }} data-testid={`${testId}-progress`} /></div>{retrying && <p className="mt-2 text-xs text-amber-300" data-testid={`${testId}-retrying`}>Hang tight — we'll keep trying for a bit before giving up.</p>}</>
        )}
      </button>
      <input ref={inputRef} type="file" accept="audio/*,.mp3,.m4a,.wav" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${testId}-input`} />
    </div>
  );
};
